import './Dlog.css';
import api from '../lib/api';
import { detectTranslatorArtifacts, sanitizeDiaryHtml } from '../lib/dlogHtml';
import { Button, Stack, IconButton, Popover, Box } from '@mui/material';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState, useRef } from 'react';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import TuneIcon from '@mui/icons-material/Tune';
import { printDiaryAsPdf, exportDiaryToDocx, makeDiaryPdfBlob } from '../lib/exporters';
import pencil from '../assets/dlogAsset/pencil.png';
import eraser from '../assets/dlogAsset/eraser.png';

/* ---- icons for action buttons ---- */
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import CheckIcon from '@mui/icons-material/Check';

/* ---- libs for client-side export/share (mobile) ---- */
import { Document, Packer, Paragraph, HeadingLevel } from 'docx';

export default function Dlog() {
    const navigate = useNavigate();
    const MAX_SELECTED_EXPORTS = 10;

    /* ---------------- top-right avatar / menu ---------------- */
    const [me, setMe] = useState({ id: null, email: '' });
    const [pfp, setPfp] = useState('');

    /* -------- Emojis -------- */
    const draftBodyRef = useRef(null);
    const editBodyRef = useRef(null);
    const [emojiAnchor, setEmojiAnchor] = useState(null);
    const [emojiTarget, setEmojiTarget] = useState(null); // 'draft' | 'edit'
    const EMOJIS = [
        '😀', '😄', '😁', '😊', '😍', '🥰', '🤣', '😂', '🙂', '😉', '😎', '🤔', '🙃', '😴', '🤤', '😢', '😭', '😡', '👏', '👌', '👍', '🙏', '🔥', '🌟', '💯', '✅', '❌', '✨', '🎉', '🥳', '📝', '📌', '📷'
    ];

    /* -------- Flips -------- */
    const [draft, setDraft] = useState({ title: '', body: '' });
    const [flipData, setFlipData] = useState({ date: '', title: '', body: '' });
    const [isFlipping, setIsFlipping] = useState(false);
    const [pendingView, setPendingView] = useState(null); // ✅ single source of truth

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const meRes = await api.get('/me');
                if (alive) setMe(meRes.data);
            } catch { }
        })();
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await api.get('/profile');
                if (!alive) return;
                setPfp(res.data?.pfp || '');
            } catch { }
        })();
        return () => { alive = false; };
    }, []);

    const logout = async () => {
        try {
            await api.post('/logout');
            navigate('/');
        } catch {
            alert('Logout failed');
        }
    };

    /* ---------------- diaries (backend persistence) ---------------- */
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [selectionMode, setSelectionMode] = useState('none'); // 'none' | 'delete' | 'export'
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [exportingSelected, setExportingSelected] = useState(false);
    const [exportFormat, setExportFormat] = useState('docx'); // 'docx' | 'html' | 'pdf'
    const [exportStatus, setExportStatus] = useState('');
    const [editorStyle, setEditorStyle] = useState({
        fontSize: 16,
        color: '#222222',
        bold: false,
        underline: false,
        highlight: '#fff59d',
    });
    const [isMobile, setIsMobile] = useState(false);
    const [helperOpen, setHelperOpen] = useState(true);
    const MAX_WORDS = 1000;
    const MAX_TITLE_CHARS = 30;
    const [wordCounts, setWordCounts] = useState({ draft: 0, edit: 0 });
    const lastGoodHtmlRef = useRef({ draft: '', edit: '' });
    const [paintMode, setPaintMode] = useState(false);
    const [paintStyle, setPaintStyle] = useState({
        fontSize: 16,
        color: '#222222',
        bold: false,
        underline: false,
        highlight: '#fff59d',
    });
    const lastSelectionStyleRef = useRef(editorStyle);
    const [formatTarget, setFormatTarget] = useState('draft'); // 'draft' | 'edit'

    const prepareDiaryBodyForSave = (html, target) => {
        const rawHtml = String(html || '');
        if (detectTranslatorArtifacts(rawHtml)) {
            console.warn(`[Dlog] Translator artifacts detected before ${target} save.`);
        }
        return sanitizeDiaryHtml(rawHtml);
    };

    const loadPosts = async () => {
        try {
            setLoading(true);
            setErr('');
            const res = await api.get('/blog/posts');
            setPosts(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setErr('Could not load diaries. Are you signed in?');
            setPosts([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadPosts(); }, []);

    /* ---------------- left-page manager ---------------- */
    const [q, setQ] = useState('');
    const stripHtml = (html = '') => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return posts;
        return posts.filter(
            (p) => (p.title || '').toLowerCase().includes(s) || stripHtml(p.body || '').toLowerCase().includes(s)
        );
    }, [q, posts]);
    const isSelectionMode = selectionMode !== 'none';
    const isDeleteMode = selectionMode === 'delete';
    const isExportMode = selectionMode === 'export';

    const getDiaryTitleForMessage = (id) => {
        const post = posts.find((item) => item.id === id);
        return post?.title?.trim() || '(Untitled)';
    };

    const toggleSelectedDiary = (id) => {
        setSelectedIds((prev) => {
            if (!prev.has(id) && isExportMode && prev.size >= MAX_SELECTED_EXPORTS) {
                alert(`You can export up to ${MAX_SELECTED_EXPORTS} diaries at a time.`);
                return prev;
            }
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const clearSelectionMode = () => {
        setSelectedIds(new Set());
        setSelectionMode('none');
        setExportFormat('docx');
        setExportStatus('');
    };

    const deleteSelectedPosts = async () => {
        const ids = Array.from(selectedIds);
        const deleted = [];
        const failed = [];
        for (const id of ids) {
            try {
                const res = await api.delete(`/blog/posts/${id}`);
                if (res.status !== 204 && res.status !== 200) throw new Error('Bad status');
                deleted.push(id);
            } catch {
                failed.push(id);
            }
        }

        if (deleted.length) {
            const removedSet = new Set(deleted);
            setPosts((ps) => ps.filter((p) => !removedSet.has(p.id)));
            if (viewing && removedSet.has(viewing.id)) {
                setViewing(null);
                setIsEditing(false);
            }
        }
        if (failed.length) alert(`Failed to delete ${failed.length} item(s). Please try again.`);
        clearSelectionMode();
    };

    const handleGroupDeleteClick = async () => {
        if (!isDeleteMode) {
            setSelectedIds(new Set());
            setSelectionMode('delete');
            return;
        }
        if (selectedIds.size === 0) {
            clearSelectionMode();
            return;
        }
        if (!confirm(`Delete ${selectedIds.size} selected ${selectedIds.size === 1 ? 'entry' : 'entries'}?`)) return;
        await deleteSelectedPosts();
    };

    const handleDeleteSelectionConfirm = async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`Delete ${selectedIds.size} selected ${selectedIds.size === 1 ? 'entry' : 'entries'}?`)) return;
        await deleteSelectedPosts();
    };

    const getFilenameFromDisposition = (headerValue, fallback) => {
        const value = String(headerValue || '');
        const encodedMatch = value.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
        if (encodedMatch) {
            try {
                return decodeURIComponent(encodedMatch[1]);
            } catch {
                // fall through
            }
        }

        const quotedMatch = value.match(/filename\s*=\s*"([^"]+)"/i);
        if (quotedMatch) return quotedMatch[1];

        const plainMatch = value.match(/filename\s*=\s*([^;]+)/i);
        if (plainMatch) return plainMatch[1].trim();

        return fallback;
    };

    const downloadBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const exportSelectedPosts = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) {
            setExportStatus('Select at least 1 diary to export.');
            return;
        }

        if (ids.length > MAX_SELECTED_EXPORTS) {
            setExportStatus(`You can export up to ${MAX_SELECTED_EXPORTS} diaries at a time.`);
            alert(`You can export up to ${MAX_SELECTED_EXPORTS} diaries at a time.`);
            return;
        }

        try {
            setExportingSelected(true);
            if (exportFormat === 'pdf') {
                setExportStatus(`Generating ${ids.length} PDF${ids.length === 1 ? '' : 's'}...`);
                const [{ default: JSZip }] = await Promise.all([
                    import('jszip'),
                ]);
                const zip = new JSZip();
                const usedNames = new Set();
                const selectedPosts = ids
                    .map((id) => posts.find((post) => post.id === id))
                    .filter(Boolean);

                if (selectedPosts.length === 0) {
                    throw new Error('No selected diaries could be found for PDF export.');
                }

                for (let index = 0; index < selectedPosts.length; index += 1) {
                    const post = selectedPosts[index];
                    setExportStatus(`Generating PDF ${index + 1} of ${selectedPosts.length}...`);
                    try {
                        const pdfBlob = await makeDiaryPdfBlob({
                            title: post.title || '',
                            bodyHtml: post.body || '',
                            bodyText: stripHtml(post.body || ''),
                            dateString: formatDate(post.createdAt),
                            author: me?.email || '',
                        });
                        zip.file(makeExportFilename(post, 'pdf', usedNames), pdfBlob);
                    } catch (error) {
                        const diaryTitle = getDiaryTitleForMessage(post.id);
                        throw new Error(`Failed to generate PDF for "${diaryTitle}".`);
                    }
                }

                setExportStatus('Packaging ZIP...');
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                downloadBlob(zipBlob, `dlog-selected-${new Date().toISOString().slice(0, 10)}-pdf.zip`);
            } else {
                const format = exportFormat === 'html' ? 'html' : 'docx';
                setExportStatus(`Preparing ${format.toUpperCase()} ZIP...`);
                const res = await api.post('/blog/posts/export-selected', { ids, format }, { responseType: 'blob' });
                const filename = getFilenameFromDisposition(
                    res.headers?.['content-disposition'],
                    `dlog-selected-${new Date().toISOString().slice(0, 10)}-${format}.zip`
                );
                downloadBlob(res.data, filename);
            }
            clearSelectionMode();
        } catch (error) {
            console.error('Failed to export selected diaries:', error);
            setExportStatus(error?.message || 'Failed to export selected diaries.');
            alert(error?.message || 'Failed to export selected diaries.');
        } finally {
            setExportingSelected(false);
        }
    };

    const handleExportSelectedClick = async () => {
        if (!isExportMode) {
            setSelectedIds(new Set());
            setSelectionMode('export');
            setExportFormat('docx');
            return;
        }

        if (selectedIds.size === 0 || exportingSelected) {
            return;
        }

        await exportSelectedPosts();
    };

    /* ---------------- right-page modes: view / edit / compose ---------------- */
    const [viewing, setViewing] = useState(null); // entry | null
    const [isEditing, setIsEditing] = useState(false);
    const [editDraft, setEditDraft] = useState({ title: '', body: '' });

    const startEdit = () => {
        if (!viewing) return;
        const limitedTitle = enforceTitleLimit(viewing.title || '');
        setEditDraft({ title: limitedTitle, body: viewing.body || '' });
        titleLimitWarnedRef.current.edit = false;
        const w = countWords(viewing.body || '');
        setWordCounts((s) => ({ ...s, edit: w }));
        lastGoodHtmlRef.current.edit = viewing.body || '';
        setIsEditing(true);
    };
    const cancelEdit = () => setIsEditing(false);

    function canFlip() {
        const el = document.querySelector('.flip-page');
        if (!el) return false;
        const cs = window.getComputedStyle(el);
        return cs.display !== 'none' && cs.visibility !== 'hidden';
    }

    const saveEdit = async () => {
        if (!viewing) return;
        const payload = {
            title: enforceTitleLimit(editDraft.title).trim(),
            body: prepareDiaryBodyForSave(editDraft.body, 'edit').trim(),
        };
        try {
            const res = await api.put(`/blog/posts/${viewing.id}`, payload);
            const updated = res.data;
            setPosts((ps) => ps.map((p) => (p.id === viewing.id ? updated : p)));

            if (!canFlip()) { setViewing(updated); setIsEditing(false); return; }

            setFlipData({
                date: new Date(updated.createdAt).toLocaleDateString(),
                title: updated.title || '(Untitled)',
                body: stripHtml(updated.body || ''),
            });
            setViewing(null);
            setIsEditing(false);
            setDraft({ title: '', body: '' });
            setIsFlipping(false);
            requestAnimationFrame(() => requestAnimationFrame(() => setIsFlipping(true)));
        } catch {
            alert('Failed to save changes.');
        }
    };

    /* -------- emoji insertion helpers -------- */
    function insertEmojiAtCursor(emoji) {
        const target = emojiTarget === 'edit' ? 'edit' : 'draft';
        const el = getTargetEl(target);
        if (!el) return;
        setFormatTarget(target);
        ensureSelectionWithinTarget(target);
        document.execCommand('insertText', false, emoji);
        syncBodyFromDom(target);
    }

    /* ---------------- compose + flip animation ---------------- */
    const saveDraftIfAny = async () => {
        const title = enforceTitleLimit(draft.title).trim();
        const body = prepareDiaryBodyForSave(draft.body, 'draft').trim();
        if (!title && !stripHtml(body)) return null;
        try {
            const res = await api.post('/blog/posts', { title, body });
            const entry = res.data;
            setPosts((p) => [entry, ...p]);
            setDraft({ title: '', body: '' });
            titleLimitWarnedRef.current.draft = false;
            lastGoodHtmlRef.current.draft = '';
            setWordCounts((s) => ({ ...s, draft: 0 }));
            return entry;
        } catch {
            alert('Failed to save entry.');
            return null;
        }
    };

    const handleFlipSave = async () => {
        if (isFlipping) return;

        setFlipData({
            date: new Date().toLocaleDateString(),
            title: enforceTitleLimit(draft.title) || 'Title…',
            body: stripHtml(draft.body || '') || 'Write your diary…',
        });

        const saved = await saveDraftIfAny();
        if (!saved) return;

        if (!canFlip()) {
            setViewing(saved);
            setPendingView(null);
            setIsEditing(false);
            return;
        }

        setPendingView(saved);
        setIsFlipping(true);
    };

    const handleFlipToEntry = (entry) => {
        if (!isFlipping) {
            setViewing(entry);
            setPendingView(null);
            setIsEditing(false);
        }
    };

    const handleFlipEnd = () => {
        setIsFlipping(false);
        if (pendingView) {
            setViewing(pendingView);
            setPendingView(null);
            setIsEditing(false);
        }
    };

    /*------------Diary Export (shared)------------*/
    function currentDiary() {
        const created = viewing?.createdAt || new Date().toISOString();
        if (viewing && !isEditing) {
            const html = (viewing?.body ?? '').trim();
            return {
                ...viewing,
                title: (viewing?.title ?? '').trim(),
                bodyHtml: html,
                bodyText: stripHtml(html),
                createdAt: created,
            };
        }
        if (isEditing) {
            const html = (editDraft?.body ?? '').trim();
            return {
                title: (editDraft?.title ?? '').trim(),
                bodyHtml: html,
                bodyText: stripHtml(html),
                createdAt: created,
            };
        }
        const html = (draft?.body ?? '').trim();
        return {
            title: (draft?.title ?? '').trim(),
            bodyHtml: html,
            bodyText: stripHtml(html),
            createdAt: created,
        };
    }
    function formatDate(iso) { try { return new Date(iso).toLocaleString(); } catch { return ''; } }
    const formatDateForFilename = (value) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return 'undated';
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };
    const sanitize = (name) => {
        const normalized = String(name || 'Diary')
            .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/[. ]+$/g, '')
            .slice(0, 80);
        return normalized || 'Diary';
    };
    const makeExportFilename = (post, extension, usedNames = null) => {
        const datePart = formatDateForFilename(post?.createdAt);
        const titlePart = sanitize(post?.title || 'Diary');
        const base = `${datePart} - ${titlePart}`.slice(0, 120).trim() || 'Diary';
        let candidate = `${base}.${extension}`;
        if (!usedNames) return candidate;
        let counter = 2;
        while (usedNames.has(candidate)) {
            candidate = `${base} (${counter}).${extension}`;
            counter += 1;
        }
        usedNames.add(candidate);
        return candidate;
    };

    async function makeDocxBlob(title, body) {
        const doc = new Document({
            sections: [{
                children: [
                    new Paragraph({ text: title, heading: HeadingLevel.HEADING_1 }),
                    ...String(body || '').split(/\n+/).map(line => new Paragraph({ text: line }))
                ]
            }]
        });
        return await Packer.toBlob(doc);
    }

    async function shareOrDownload(blob, filename, mime, preferShare = true) {
        const file = new File([blob], filename, { type: mime });

        // Some browsers won't share Office docs; preferShare lets us opt out.
        const canShareFiles = typeof navigator !== 'undefined'
            && !!navigator.canShare
            && navigator.canShare({ files: [file] });

        if (preferShare && canShareFiles) {
            try {
                await navigator.share({ files: [file], title: filename, text: 'Shared from Dlog' });
                return; // success
            } catch (err) {
                // If user cancels, just return silently; if blocked, we fall through to download
                if (err?.name === 'AbortError') return;
                console.warn('Share failed; falling back to download:', err);
            }
        }

        // Fallback: direct download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }


    const [exportOpen, setExportOpen] = useState(false);
    async function handleMobileExport(format) {
        try {
            const d = currentDiary();
            const title = sanitize(d.title || 'Diary');
            if (format === 'docx') {
                const blob = await makeDocxBlob(title, d.bodyText || '');
                await shareOrDownload(
                    blob,
                    `${title}.docx`,
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    /* preferShare */ false  // ✅ avoid NotAllowedError on iOS
                );
            } else {
                const blob = await makeDiaryPdfBlob({
                    title: d.title,
                    bodyHtml: d.bodyHtml,
                    bodyText: d.bodyText,
                    dateString: formatDate(d.createdAt),
                    author: me?.email || '',
                });
                await shareOrDownload(blob, `${title}.pdf`, 'application/pdf', /* preferShare */ true);
            }

        } catch (err) {
            console.error(err);
            alert('Export failed. Please try again.');
        } finally {
            setExportOpen(false);
        }
    }

    const getTargetEl = (target) => (target === 'edit' ? editBodyRef.current : draftBodyRef.current);
    const countWords = (html) => {
        const text = stripHtml(html || '');
        if (!text) return 0;
        return text.trim().split(/\s+/).filter(Boolean).length;
    };
    const enforceTitleLimit = (title) => {
        const t = title || '';
        if (t.length <= MAX_TITLE_CHARS) return t;
        return t.slice(0, MAX_TITLE_CHARS);
    };
    const titleLimitWarnedRef = useRef({ draft: false, edit: false });
    const TITLE_LIMIT_MSG = `Title can only have ${MAX_TITLE_CHARS} characters (including spaces).`;
    const clampTitleInput = (value, target) => {
        const limited = enforceTitleLimit(value);
        const len = (value || '').length;
        if (len > MAX_TITLE_CHARS && !titleLimitWarnedRef.current[target]) {
            alert(TITLE_LIMIT_MSG);
            titleLimitWarnedRef.current[target] = true;
        }
        if (len <= MAX_TITLE_CHARS) titleLimitWarnedRef.current[target] = false;
        return limited;
    };
    const handleTitleChange = (e, target) => {
        const limited = clampTitleInput(e.target.value, target);
        if (limited !== e.target.value) e.target.value = limited;
        if (target === 'edit') setEditDraft((s) => ({ ...s, title: limited }));
        else setDraft((s) => ({ ...s, title: limited }));
    };
    const handleTitleBeforeInput = (e, target) => {
        const current = target === 'edit' ? editDraft.title : draft.title;
        const data = typeof e.data === 'string' ? e.data : (e.clipboardData?.getData('text') || '');
        if (!data) return; // allow deletions/controls
        const { selectionStart = current.length, selectionEnd = current.length } = e.target;
        const next = current.slice(0, selectionStart) + data + current.slice(selectionEnd);
        if (next.length > MAX_TITLE_CHARS) {
            e.preventDefault();
            if (!titleLimitWarnedRef.current[target]) {
                alert(TITLE_LIMIT_MSG);
                titleLimitWarnedRef.current[target] = true;
            }
        }
    };
    const captureSelectionStyle = (target) => {
        const base = { ...editorStyle };
        const sel = window.getSelection();
        const el = getTargetEl(target);
        if (!sel || !el || !sel.rangeCount || !el.contains(sel.anchorNode)) return base;
        let node = sel.anchorNode instanceof Element ? sel.anchorNode : sel.anchorNode?.parentElement;
        while (node && node !== el && !(node instanceof HTMLElement)) node = node.parentElement;
        if (!node || !(node instanceof HTMLElement)) return base;
        const cs = window.getComputedStyle(node);
        return {
            fontSize: parseFloat(cs.fontSize) || base.fontSize,
            color: cs.color || base.color,
            bold: (parseInt(cs.fontWeight, 10) || 400) >= 600,
            underline: cs.textDecoration.includes('underline'),
            highlight: cs.backgroundColor && cs.backgroundColor !== 'rgba(0, 0, 0, 0)' ? cs.backgroundColor : base.highlight,
        };
    };

    const syncBodyFromDom = (target) => {
        const el = getTargetEl(target);
        if (!el) return;
        const html = typeof el.innerHTML === 'string' ? el.innerHTML : '';
        if (target === 'edit') {
            setEditDraft((s) => ({ ...s, body: html }));
            lastGoodHtmlRef.current.edit = html;
            setWordCounts((s) => ({ ...s, edit: countWords(html) }));
        } else {
            setDraft((s) => ({ ...s, body: html }));
            lastGoodHtmlRef.current.draft = html;
            setWordCounts((s) => ({ ...s, draft: countWords(html) }));
        }
    };

    const syncDomFromState = (target) => {
        const el = getTargetEl(target);
        if (!el) return;
        const html = target === 'edit' ? (editDraft.body || '') : (draft.body || '');
        if (el.innerHTML !== html) el.innerHTML = html;
    };

    const ensureSelectionWithinTarget = (target) => {
        const el = getTargetEl(target);
        if (!el) return null;
        el.focus();
        const sel = window.getSelection();
        if (!sel) return null;
        if (!sel.rangeCount || !el.contains(sel.anchorNode)) {
            const r = document.createRange();
            r.selectNodeContents(el);
            r.collapse(false);
            sel.removeAllRanges();
            sel.addRange(r);
        }
        return { el, sel };
    };

    const applyInlineStyle = (styleObj, target = formatTarget) => {
        const ctx = ensureSelectionWithinTarget(target);
        if (!ctx) return;
        const { sel } = ctx;
        if (!sel.rangeCount) return;
        const range = sel.getRangeAt(0);
        const el = getTargetEl(target);
        if (!el || !el.contains(range.commonAncestorContainer)) return;

        const span = document.createElement('span');
        Object.assign(span.style, styleObj);

        if (range.collapsed) {
            // Insert a styled span with a zero-width space so future typing inherits the style
            const textNode = document.createTextNode('\u200b');
            span.appendChild(textNode);
            range.insertNode(span);
            const r = document.createRange();
            r.setStart(textNode, textNode.length);
            r.collapse(true);
            sel.removeAllRanges();
            sel.addRange(r);
        } else {
            try {
                range.surroundContents(span);
            } catch {
                const frag = range.extractContents();
                span.appendChild(frag);
                range.insertNode(span);
            }
            sel.removeAllRanges();
            const r = document.createRange();
            r.selectNodeContents(span);
            sel.addRange(r);
        }
        syncBodyFromDom(target);
    };

    const applyCommand = (cmd, value) => {
        const ctx = ensureSelectionWithinTarget(formatTarget);
        if (!ctx) return;
        document.execCommand(cmd, false, value);
        syncBodyFromDom(formatTarget);
    };

    const handleFontSizeChange = (px) => {
        setEditorStyle((s) => ({ ...s, fontSize: px }));
        applyInlineStyle({ fontSize: `${px}px` });
    };

    const handleColorChange = (color) => {
        setEditorStyle((s) => ({ ...s, color }));
        applyInlineStyle({ color });
    };

    const handleHighlightChange = (color) => {
        setEditorStyle((s) => ({ ...s, highlight: color }));
        applyInlineStyle({ backgroundColor: color });
    };

    const clearHighlight = () => {
        const target = formatTarget;
        const ctx = ensureSelectionWithinTarget(target);
        if (!ctx) return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const el = getTargetEl(target);
        if (!el) return;

        const range = sel.getRangeAt(0);
        if (!range.collapsed && el.contains(range.commonAncestorContainer)) {
            document.execCommand('hiliteColor', false, 'transparent');
            document.execCommand('backColor', false, 'transparent');
        }

        // Scrub highlight styles on the caret container so new typing does not inherit
        let node = sel.focusNode instanceof Element ? sel.focusNode : sel.focusNode?.parentElement;
        while (node && node !== el) {
            if (node instanceof HTMLElement && node.style.backgroundColor) {
                node.style.backgroundColor = '';
                if (!node.getAttribute('style')) node.removeAttribute('style');
            }
            node = node.parentElement;
        }

        // Move caret to a clean text node outside any highlight span
        const caretText = document.createTextNode('');
        const caretRange = sel.getRangeAt(0);
        caretRange.collapse(false);
        caretRange.insertNode(caretText);
        const newRange = document.createRange();
        newRange.setStart(caretText, 0);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);

        setEditorStyle((s) => ({ ...s, highlight: '#fff59d' }));
        syncBodyFromDom(target);
        setPaintMode(false);
    };

    const toggleBold = () => {
        applyCommand('bold');
        setEditorStyle((s) => ({ ...s, bold: document.queryCommandState('bold') }));
    };

    const toggleUnderline = () => {
        applyCommand('underline');
        setEditorStyle((s) => ({ ...s, underline: document.queryCommandState('underline') }));
    };

    const activatePaintMode = () => {
        if (paintMode) { setPaintMode(false); return; }
        const sel = window.getSelection();
        const hasRange = sel && sel.rangeCount;
        const range = hasRange ? sel.getRangeAt(0) : null;
        const canCopy = range && !range.collapsed;
        const style = canCopy ? captureSelectionStyle(formatTarget) : lastSelectionStyleRef.current;
        if (style) {
            setPaintStyle(style);
            lastSelectionStyleRef.current = style;
            setPaintMode(true);
        }
    };

    const applyPaintIfNeeded = (target) => {
        if (!paintMode) return;
        const ctx = ensureSelectionWithinTarget(target);
        if (!ctx) return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);
        if (range.collapsed) return; // wait for a real selection
        setFormatTarget(target);
        applyInlineStyle({
            fontSize: `${paintStyle.fontSize}px`,
            color: paintStyle.color,
            fontWeight: paintStyle.bold ? '700' : '400',
            textDecoration: paintStyle.underline ? 'underline' : 'none',
            backgroundColor: paintStyle.highlight,
        }, target);
        setPaintMode(false);
    };

    useEffect(() => {
        const handleSelectionChange = () => {
            const sel = window.getSelection();
            const el = getTargetEl(formatTarget);
            if (!sel || !el || !el.contains(sel.anchorNode)) return;
            setEditorStyle((s) => ({
                ...s,
                bold: document.queryCommandState('bold'),
                underline: document.queryCommandState('underline'),
            }));
        };
        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, [formatTarget]);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 900px)');
        const onChange = () => {
            const mobile = mq.matches;
            setIsMobile(mobile);
            setHelperOpen(!mobile);
        };
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    const helperRef = useRef(null);

    useEffect(() => {
        // Keep helper targeting the active editor
        setFormatTarget(isEditing ? 'edit' : 'draft');
    }, [isEditing]);

    useEffect(() => { syncDomFromState('draft'); }, [draft.body, viewing]);
    useEffect(() => { syncDomFromState('edit'); }, [editDraft.body, isEditing, viewing]);

    useEffect(() => {
        const limited = enforceTitleLimit(draft.title);
        if (limited !== draft.title) {
            if (!titleLimitWarnedRef.current.draft) {
                alert(TITLE_LIMIT_MSG);
                titleLimitWarnedRef.current.draft = true;
            }
            setDraft((s) => ({ ...s, title: limited }));
        }
    }, [draft.title]);

    useEffect(() => {
        const limited = enforceTitleLimit(editDraft.title);
        if (limited !== editDraft.title) {
            if (!titleLimitWarnedRef.current.edit) {
                alert(TITLE_LIMIT_MSG);
                titleLimitWarnedRef.current.edit = true;
            }
            setEditDraft((s) => ({ ...s, title: limited }));
        }
    }, [editDraft.title]);

    const canFormat = isEditing || !viewing;
    const showHelper = canFormat && (!isMobile || helperOpen);

    return (
        <div
            className={`diary-stage notranslate${paintMode ? ' paint-mode' : ''}`}
            translate="no"
        >
            {/*------Profile Button------*/}
            <button
                type="button"
                className="Dlog-profile-button"
                onClick={() => navigate('/Dashboard/ProfilePage')}
                style={pfp ? { backgroundImage: `url(${pfp})` } : undefined}
                title="Open profile"
            >
                {!pfp ? (me.email?.[0]?.toUpperCase() || 'U') : null}
            </button>

            <button type="button" className="Dlog-dashboard-button" onClick={() => navigate('/Dashboard')}>
                <ArrowBackIosNewIcon sx={{ fontSize: 20, color: 'white' }} />
            </button>

            <div className="book">
                {/*---------LEFT---------*/}
                <section className="page page-left">
                    <div className="manager-head">
                        <strong style={{ color: 'black' }}>My Diaries</strong>
                        <input
                            placeholder={`Search… (a total of ${posts.length} diaries)`}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            style={{ flex: 1 }}
                        />
                        <div className="manager-actions">
                            {isDeleteMode ? (
                                <div className="group-delete-mode" aria-label="Delete selection controls">
                                    <IconButton
                                        color="inherit"
                                        size="small"
                                        onClick={clearSelectionMode}
                                        className="group-delete-cancel-btn"
                                        title="Cancel delete selection"
                                        aria-label="Cancel delete selection"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        size="small"
                                        onClick={handleDeleteSelectionConfirm}
                                        disabled={selectedIds.size === 0}
                                        className={`group-delete-confirm-btn${selectedIds.size === 0 ? ' muted' : ''}`}
                                        title="Delete selected diaries"
                                        aria-label="Confirm delete selection"
                                    >
                                        <CheckIcon />
                                    </IconButton>
                                </div>
                            ) : null}

                            {isExportMode ? (
                                <div className="group-export-mode" aria-label="Export selection controls">
                                    <div className="group-export-formats" role="group" aria-label="Export format">
                                        <button
                                            type="button"
                                            className={`group-export-format-btn${exportFormat === 'docx' ? ' active' : ''}`}
                                            onClick={() => setExportFormat('docx')}
                                        >
                                            DOCX
                                        </button>
                                        <button
                                            type="button"
                                            className={`group-export-format-btn${exportFormat === 'html' ? ' active' : ''}`}
                                            onClick={() => setExportFormat('html')}
                                        >
                                            HTML
                                        </button>
                                        <button
                                            type="button"
                                            className={`group-export-format-btn${exportFormat === 'pdf' ? ' active' : ''}`}
                                            onClick={() => setExportFormat('pdf')}
                                            title="Export selected diaries as a ZIP of PDFs"
                                        >
                                            PDF
                                        </button>
                                    </div>
                                    <span className="group-export-count">
                                        {selectedIds.size} / {MAX_SELECTED_EXPORTS} selected
                                    </span>
                                    <IconButton
                                        color="inherit"
                                        size="small"
                                        onClick={clearSelectionMode}
                                        className="group-export-cancel-btn"
                                        title="Cancel export selection"
                                        aria-label="Cancel export selection"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                    <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={handleExportSelectedClick}
                                        disabled={selectedIds.size === 0 || exportingSelected}
                                        className={`group-export-confirm-btn${selectedIds.size === 0 ? ' muted' : ''}`}
                                        title={`Download selected diaries as ${exportFormat.toUpperCase()} ZIP`}
                                        aria-label="Confirm export selection"
                                    >
                                        <CheckIcon />
                                    </IconButton>
                                </div>
                            ) : null}

                            {!isSelectionMode ? (
                                <>
                                    <IconButton
                                        color="error"
                                        size="small"
                                        onClick={handleGroupDeleteClick}
                                        className="group-delete-btn"
                                        title="Group delete"
                                        aria-label="Group delete"
                                    >
                                        <DeleteForeverIcon />
                                    </IconButton>
                                    <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={handleExportSelectedClick}
                                        disabled={loading}
                                        className="group-export-btn"
                                        title="Group export"
                                        aria-label="Group export"
                                    >
                                        <DownloadIcon />
                                    </IconButton>
                                </>
                            ) : null}
                        </div>
                    </div>

                    {err && <div className="diary-card" style={{ gridColumn: 'span 2', color: '#b00' }}>{err}</div>}
                    {isExportMode && exportStatus && (
                        <div className="export-mode-note" style={{ gridColumn: 'span 2' }}>
                            <span>{exportStatus}</span>
                        </div>
                    )}

                    <div className="diary-list">
                        {loading ? (
                            <div className="diary-card" style={{ gridColumn: 'span 2' }}><p>Loading…</p></div>
                        ) : filtered.length === 0 ? (
                            <div className="diary-card" style={{ gridColumn: 'span 2' }}><p>No entries yet. Write one →</p></div>
                        ) : (
                            filtered.map((p) => (
                                <article
                                    key={p.id}
                                    className={`diary-card${isSelectionMode ? ' multi-mode' : ''}${selectedIds.has(p.id) ? ' selected' : ''}`}
                                    onClick={() => { isSelectionMode ? toggleSelectedDiary(p.id) : handleFlipToEntry(p); }}
                                    title="Open this diary"
                                >
                                    {isSelectionMode ? (
                                        <label className="diary-select" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(p.id)}
                                                onChange={() => toggleSelectedDiary(p.id)}
                                                aria-label={isDeleteMode ? 'Select diary for deletion' : 'Select diary for export'}
                                            />
                                        </label>
                                    ) : null}

                                    <div className="card-head">
                                        <h4>{p.title || '(Untitled)'}</h4>
                                        <time>{new Date(p.createdAt).toLocaleDateString()}</time>
                                    </div>
                                    <p>{stripHtml(p.body || '')}</p>
                                </article>
                            ))
                        )}
                    </div>
                </section>

                {/*---------RIGHT---------*/}
                <section id="dlog-current-page" className="page page-right">
                    {viewing ? (
                        !isEditing ? (
                            <>
                                <h2 className="diary-title single-line" style={{ userSelect: 'text' }}>
                                    {viewing.title || '(Untitled)'}
                                </h2>
                                <div
                                    className="diary-textarea notranslate"
                                    style={{ userSelect: 'text' }}
                                    translate="no"
                                    dangerouslySetInnerHTML={{ __html: viewing.body || '' }}
                                />

                                {/* Action buttons pinned bottom-right (desktop visible) */}
                                <Stack direction="row" spacing={1} className="diary-actions">
                                    <Button variant="contained" size="small" startIcon={<EditIcon />} onClick={startEdit}>
                                        Edit
                                    </Button>
                                    <Button variant="contained" size="small" startIcon={<NoteAddIcon />} onClick={() => setViewing(null)}>
                                        New
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<PictureAsPdfIcon />}
                                        onClick={() => {
                                            const d = currentDiary();
                                            printDiaryAsPdf({
                                                title: d.title,
                                                bodyHtml: d.bodyHtml,
                                                bodyText: d.bodyText,
                                                dateString: formatDate(d.createdAt),
                                                author: me?.email || '',
                                            });
                                        }}
                                    >
                                        PDF
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<DescriptionIcon />}
                                        onClick={async () => {
                                            const d = currentDiary();
                                            await exportDiaryToDocx({
                                                title: d.title,
                                                bodyHtml: d.bodyHtml,
                                                bodyText: d.bodyText,
                                                dateString: formatDate(d.createdAt),
                                                author: me?.email || '',
                                            });
                                        }}
                                    >
                                        DOCX
                                    </Button>
                                </Stack>
                            </>
                        ) : (
                            <>
                                <input
                                className="diary-title single-line"
                                placeholder="Title…"
                                value={editDraft.title}
                                    onChange={(e) => handleTitleChange(e, 'edit')}
                                    onBeforeInput={(e) => handleTitleBeforeInput(e, 'edit')}
                                />
                                <div
                                    className="diary-textarea notranslate"
                                    contentEditable
                                    suppressContentEditableWarning
                                    data-placeholder="Write your diary…"
                                    translate="no"
                                    onInput={(e) => {
                                        const html = e.currentTarget?.innerHTML ?? '';
                                        const words = countWords(html);
                                        if (words > MAX_WORDS) {
                                            const safe = lastGoodHtmlRef.current.edit || '';
                                            e.currentTarget.innerHTML = safe;
                                            const range = document.createRange();
                                            range.selectNodeContents(e.currentTarget);
                                            range.collapse(false);
                                            const sel = window.getSelection();
                                            if (sel) { sel.removeAllRanges(); sel.addRange(range); }
                                            return;
                                        }
                                        setEditDraft((s) => ({ ...s, body: html }));
                                        lastGoodHtmlRef.current.edit = html;
                                        setWordCounts((s) => ({ ...s, edit: words }));
                                    }}
                                    onMouseUp={() => applyPaintIfNeeded('edit')}
                                    ref={editBodyRef}
                                    onFocus={() => setFormatTarget('edit')}
                                />

                                {/* Emoji + actions pinned together */}
                                <div className="action-bar">
                                    <div className="action-left">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => { setEmojiAnchor(e.currentTarget); setEmojiTarget('edit'); }}
                                            title="Insert emoji"
                                        >
                                            <InsertEmoticonIcon />
                                        </IconButton>
                                        {isMobile && canFormat && !helperOpen && (
                                            <button
                                                type="button"
                                                className="helper-toggle"
                                                onClick={() => setHelperOpen(true)}
                                                aria-label="Open formatting helper"
                                            >
                                                <TuneIcon fontSize="small" />
                                            </button>
                                        )}
                                        <span className="word-count inline">Words: {wordCounts.edit}/{MAX_WORDS}</span>
                                    </div>
                                    <Stack direction="row" spacing={1} className="diary-actions">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<SaveIcon />}
                                            onClick={saveEdit}
                                            disabled={!editDraft.title.trim() && !stripHtml(editDraft.body || '').trim()}
                                        >
                                            Save changes
                                        </Button>
                                        <Button variant="outlined" size="small" onClick={cancelEdit}>Cancel</Button>
                                    </Stack>
                                </div>
                            </>
                        )
                    ) : (
                        <>
                            <input
                                className="diary-title single-line"
                                placeholder="Title…"
                                value={draft.title}
                                onChange={(e) => handleTitleChange(e, 'draft')}
                                onBeforeInput={(e) => handleTitleBeforeInput(e, 'draft')}
                            />
                            <div
                                className="diary-textarea notranslate"
                                contentEditable
                                suppressContentEditableWarning
                                data-placeholder="Write your diary…"
                                translate="no"
                                onInput={(e) => {
                                    const html = e.currentTarget?.innerHTML ?? '';
                                    const words = countWords(html);
                                    if (words > MAX_WORDS) {
                                        const safe = lastGoodHtmlRef.current.draft || '';
                                        e.currentTarget.innerHTML = safe;
                                        const range = document.createRange();
                                        range.selectNodeContents(e.currentTarget);
                                        range.collapse(false);
                                        const sel = window.getSelection();
                                        if (sel) { sel.removeAllRanges(); sel.addRange(range); }
                                        return;
                                    }
                                    setDraft((s) => ({ ...s, body: html }));
                                    lastGoodHtmlRef.current.draft = html;
                                    setWordCounts((s) => ({ ...s, draft: words }));
                                }}
                                onMouseUp={() => applyPaintIfNeeded('draft')}
                                ref={draftBodyRef}
                                onFocus={() => setFormatTarget('draft')}
                            />

                            {/* Emoji + actions pinned together */}
                            <div className="action-bar">
                                <div className="action-left">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { setEmojiAnchor(e.currentTarget); setEmojiTarget('draft'); }}
                                        title="Insert emoji"
                                    >
                                        <InsertEmoticonIcon />
                                    </IconButton>
                                    {isMobile && canFormat && !helperOpen && (
                                        <button
                                            type="button"
                                            className="helper-toggle"
                                            onClick={() => setHelperOpen(true)}
                                            aria-label="Open formatting helper"
                                        >
                                            <TuneIcon fontSize="small" />
                                        </button>
                                    )}
                                    <span className="word-count inline">Words: {wordCounts.draft}/{MAX_WORDS}</span>
                                </div>
                                <Stack direction="row" spacing={1} className="diary-actions">
                                    <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleFlipSave}>
                                        Save &amp; Flip
                                    </Button>
                                </Stack>
                            </div>
                        </>
                    )}
                </section>

                {canFormat && (
                    <aside
                        className="edit-helper"
                        aria-label="Text formatting tools"
                        ref={helperRef}
                        aria-hidden={!showHelper}
                        style={{ display: showHelper ? 'flex' : 'none' }}
                    >
                        {isMobile && (
                            <button
                                type="button"
                                className="helper-close"
                                onClick={() => setHelperOpen(false)}
                                aria-label="Close helper"
                            >
                                <CloseIcon fontSize="small" />
                            </button>
                        )}
                        <div className="helper-row">
                            <label className="helper-label" htmlFor="helper-size">Size</label>
                            <select
                                id="helper-size"
                                className="helper-select"
                                value={editorStyle.fontSize}
                                onChange={(e) => handleFontSizeChange(Number(e.target.value))}
                                aria-label="Font size"
                                title="Font size"
                            >
                                {[12, 14, 16, 18, 20, 22, 24, 28].map((s) => (
                                    <option key={s} value={s}>{s}px</option>
                                ))}
                            </select>
                        </div>
                        <div className="helper-row">
                            <label className="helper-label" htmlFor="helper-color">Color</label>
                            <input
                                id="helper-color"
                                type="color"
                                className="helper-input"
                                value={editorStyle.color}
                                onChange={(e) => handleColorChange(e.target.value)}
                                aria-label="Font color"
                                title="Font color"
                            />
                        </div>
                        <div className="helper-row">
                            <label className="helper-label" htmlFor="helper-highlight">Highlight</label>
                            <input
                                id="helper-highlight"
                                type="color"
                                className="helper-input"
                                value={editorStyle.highlight}
                                onChange={(e) => handleHighlightChange(e.target.value)}
                                aria-label="Highlight color"
                                title="Highlight color"
                            />
                        </div>
                        <div className="helper-row helper-buttons">
                            <button
                                type="button"
                                className={`helper-btn${paintMode ? ' active' : ''}`}
                                onClick={activatePaintMode}
                                aria-pressed={paintMode}
                                aria-label="Paint format"
                                title="Paint format (copy style to next selection)"
                            >
                                <FormatPaintIcon fontSize="small" />
                            </button>
                            <button
                                type="button"
                                className="helper-btn"
                                onClick={clearHighlight}
                                aria-label="Clear highlight"
                                title="Clear highlight"
                            >
                                <HighlightOffIcon fontSize="small" />
                            </button>
                            <button
                                type="button"
                                className={`helper-btn${editorStyle.bold ? ' active' : ''}`}
                                onClick={toggleBold}
                                aria-pressed={editorStyle.bold}
                                aria-label="Toggle bold"
                                title="Bold"
                            >
                                B
                            </button>
                            <button
                                type="button"
                                className={`helper-btn${editorStyle.underline ? ' active' : ''}`}
                                onClick={toggleUnderline}
                                aria-pressed={editorStyle.underline}
                                aria-label="Toggle underline"
                                title="Underline"
                            >
                                U
                            </button>
                        </div>
                    </aside>
                )}

                {/*----------Flip overlay----------*/}
                <div
                    className={`flip-page${isFlipping ? ' flipping' : ''}`}
                    aria-hidden={!isFlipping}
                    onAnimationEnd={handleFlipEnd}
                >
                    <div className="flip-inner">
                        <div className="flip-meta">{flipData.date}</div>
                        <h2 className="flip-title">{flipData.title}</h2>
                        <div className="flip-body">{flipData.body}</div>
                    </div>
                    <div className="flip-shade" />
                </div>

                {/* ---------- Emoji Popover ---------- */}
                <Popover
                    open={Boolean(emojiAnchor)}
                    anchorEl={emojiAnchor}
                    onClose={() => setEmojiAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Box p={1} sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 28px)', gap: 0.75, placeItems: 'center' }}>
                        {EMOJIS.map((emo) => (
                            <button
                                key={emo}
                                className="emojiBtn"
                                onClick={() => { insertEmojiAtCursor(emo); setEmojiAnchor(null); }}
                                aria-label={`Insert ${emo}`} title={emo}
                            >
                                {emo}
                            </button>
                        ))}
                    </Box>
                </Popover>

                <img src={pencil} alt="Pencil" className="book-pencil" />
                <img src={eraser} alt="Eraser" className="book-eraser" />
            </div>

            {/* ===== MOBILE EXPORT FAB + SHEET ===== */}
            {viewing && !isEditing && (
                <>
                    <button
                        className="dlog-export-fab"
                        onClick={() => setExportOpen(true)}
                        aria-label="Export diary"
                        style={{ pointerEvents: 'auto' }}
                    >
                        <DownloadIcon />
                    </button>
                    {exportOpen && (
                        <div className="dlog-export-sheet">
                            <button className="dlog-export-opt" onClick={() => handleMobileExport('pdf')}>
                                Export &amp; Share PDF
                            </button>
                            <button className="dlog-export-opt" onClick={() => handleMobileExport('docx')}>
                                Export DOCX
                            </button>
                            <button className="dlog-export-cancel" onClick={() => setExportOpen(false)}>Cancel</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
